import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../_shared/hooks';
import { settleWrite } from './unconfirmed-write';
import { fetchLaunchedCampaigns, patchCampaignStatus, wipeDispatchLog, wipeAllDispatchLogs, relaunchCampaign } from '../api-client';

export function useLaunchedCampaigns() {
  const { apiFetch } = useApi();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['launcher', 'launched-campaigns'],
    queryFn: () => fetchLaunchedCampaigns(apiFetch),
    staleTime: 0,
  });
  return { campaigns: data ?? [], isLoading, isError };
}

export function useResetCampaignStatus() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patchCampaignStatus(apiFetch, id, 'draft'),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'launched-campaigns'] });
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', id] });
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns'] });
    },
  });
}

export function useWipeDispatchLog() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      settleWrite(
        () => wipeDispatchLog(apiFetch, id),
        () => queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', id, 'dispatch-log'] }),
      ),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', id, 'dispatch-log'] });
    },
  });
}

export function useWipeAllDispatchLogs() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      settleWrite(
        () => wipeAllDispatchLogs(apiFetch),
        () => queryClient.invalidateQueries({ queryKey: ['launcher'] }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher'] });
    },
  });
}

/** Resolves to `{ unconfirmed: true }` when the relaunch may have gone out but its response was unreadable or cut off. */
export function useRelaunchCampaign() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      settleWrite(
        () => relaunchCampaign(apiFetch, id),
        () =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', id] }),
            queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', id, 'dispatch-log'] }),
          ]),
      ),
  });
}
